// ORBAT (Order of Battle): styrke-hierarki + stridsevne-vurdering. Bygger et tre
// fra en flat enhetsliste, og ruller opp stridsevne (grønn/gul/rød/svart) fra
// personellstyrke (PERSTAT) + forsyningsstatus (LOGSTAT). Ren, testbar logikk.
// Echelon-aksen kommer fra den felles hierarki-ryggraden (hierarchy.ts) — ÉN
// kilde til sannhet for kommandonivåene (enkeltmann … brigade). ORBAT utvider
// den OPPOVER med de strategiske nivåene distrikt/forsvarsgren/FOH via
// `OrbatEchelon` (superset), uten å røre den delte `Echelon` (som ESO/RBAC
// hviler på — enkeltmann→brigade er bevisst grensen der).
import { z } from 'zod';
import { ECHELON_ORDER, ECHELON_LABEL, ECHELON_ABBR, ECHELON_SIDC, } from './hierarchy.js';
// ── Echelon utvidet for ORBAT (kommando + strategisk topp) ──────────────────
// De laveste nivåene deles med `Echelon` (ingen divergens); ORBAT legger til de
// tre strategiske toppnivåene som order-of-battle trenger for å nå helt til FOH.
/** ORBAT-echelon: hele `Echelon` (enkeltmann…brigade) + strategisk topp.
 * Base-nivåene MÅ speile `Echelon` eksakt (verifisert i test) — ingen divergens;
 * ORBAT legger bare til de tre strategiske toppnivåene order-of-battle krever. */
export const OrbatEchelon = z.enum([
    'enkeltmann', 'makkerpar', 'lag', 'tropp', 'kompani', 'bataljon', 'brigade',
    'distrikt', // HV-distrikt / større formasjon
    'forsvarsgren', // Hæren, Sjøforsvaret, Luftforsvaret, HV …
    'foh', // Forsvarets operative hovedkvarter (nasjonalt/fellesoperativt)
]);
/** Kanonisk rekkefølge, laveste → høyeste (base + strategisk topp). */
export const ORBAT_ECHELON_ORDER = [
    ...ECHELON_ORDER, 'distrikt', 'forsvarsgren', 'foh',
];
export const ORBAT_ECHELON_LABEL = {
    ...ECHELON_LABEL,
    distrikt: 'Distrikt/Formasjon',
    forsvarsgren: 'Forsvarsgren',
    foh: 'FOH',
};
/** Korte forkortelser (til tett UI / symboletiketter). */
export const ORBAT_ECHELON_ABBR = {
    ...ECHELON_ABBR,
    distrikt: 'DIST',
    forsvarsgren: 'GREN',
    foh: 'FOH',
};
/** APP-6/2525 echelon-kode (SIDC pos 12). Topp-nivåene bruker de høyere
 * formasjonskodene: I=divisjon, L=hærkorps/gren, M=kommando/region. */
export const ORBAT_ECHELON_SIDC = {
    ...ECHELON_SIDC,
    distrikt: 'I',
    forsvarsgren: 'L',
    foh: 'M',
};
/** Rang (0 = enkeltmann … høyest = foh). -1 for ukjent. */
export function orbatEchelonRank(e) {
    return ORBAT_ECHELON_ORDER.indexOf(e);
}
/** Nærmeste ORBAT-echelon under `e` (null for enkeltmann). */
export function orbatChildEchelon(e) {
    const i = orbatEchelonRank(e);
    return i > 0 ? ORBAT_ECHELON_ORDER[i - 1] : null;
}
export const OrbatUnitSchema = z.object({
    id: z.string().max(64),
    name: z.string().max(120),
    echelon: OrbatEchelon.default('lag'),
    parentId: z.string().max(64).optional(),
    /** Oppsatt (autorisert) styrke. */
    authorized: z.number().int().min(0).max(100000).default(0),
    /** APP-6D/MIL-STD-2525 SIDC-streng (symbolet som tegnes for enheten). */
    sidc: z.string().max(20).optional(),
    /** Materialisert sti (ltree-stil, f.eks. «FOH.Haeren.BrigNord.Panserbn»).
     * Utledes fra parent-kjeden av `withOrbatPaths` — lagres for rask scoping/søk. */
    path: z.string().max(512).optional(),
    /** Delegert sjef: SKADE/GNÅ-bruker-id som fører kommando over enheten. */
    commanderId: z.string().max(120).optional(),
    /** Standkvarter / lokasjon (fritekst). */
    hq: z.string().max(120).optional(),
    /** Fritt notat. */
    note: z.string().max(2000).optional(),
});
/** Bygg ORBAT-tre fra flat liste. Noder uten (kjent) forelder blir røtter.
 * Robust mot sykler: hver node besøkes én gang. */
export function buildOrbatTree(units) {
    const byId = new Map();
    for (const u of units)
        byId.set(u.id, { unit: u, children: [] });
    const roots = [];
    for (const node of byId.values()) {
        const pid = node.unit.parentId;
        const parent = pid ? byId.get(pid) : undefined;
        if (parent && parent !== node)
            parent.children.push(node);
        else
            roots.push(node);
    }
    return roots;
}
/** Samme tre, men på den generiske HierNode-formen (`value`/`children`) som
 * hierarchy.ts sine rollup-verktøy (foldTree/rollupEso) forventer. */
export function buildHierNodes(units) {
    const toHier = (n) => ({
        value: n.unit, children: n.children.map(toHier),
    });
    return buildOrbatTree(units).map(toHier);
}
// ── Materialisert sti (ltree-stil) ───────────────────────────────────────────
/** Sti-segment for én enhet = siste ledd av id-en (tokens uten punktum).
 * Seed-id-er er rene tokens (FOH, Haeren, BrigNord …) så stien blir lesbar. */
function pathSegment(id) {
    const parts = id.split('.');
    return parts[parts.length - 1] || id;
}
/**
 * Utled `path` (materialisert sti) for hver enhet fra parent-kjeden.
 * F.eks. Panserbataljonen under FOH→Hæren→Brigade Nord → «FOH.Haeren.BrigNord.Panserbn».
 * Ren funksjon; robust mot sykler og manglende foreldre.
 */
export function withOrbatPaths(units) {
    const byId = new Map(units.map((u) => [u.id, u]));
    const cache = new Map();
    const pathOf = (u, seen) => {
        const hit = cache.get(u.id);
        if (hit)
            return hit;
        if (seen.has(u.id))
            return pathSegment(u.id); // syklus → stopp
        seen.add(u.id);
        const parent = u.parentId && u.parentId !== u.id ? byId.get(u.parentId) : undefined;
        const p = parent ? `${pathOf(parent, seen)}.${pathSegment(u.id)}` : pathSegment(u.id);
        cache.set(u.id, p);
        return p;
    };
    return units.map((u) => ({ ...u, path: pathOf(u, new Set()) }));
}
// ── Event sourcing (opprett/oppdater/flytt/slett/deleger) ────────────────────
/** Redigerbare felt (delta ved «oppdater»). */
export const OrbatUnitPatchSchema = z.object({
    name: z.string().max(120).optional(),
    echelon: OrbatEchelon.optional(),
    sidc: z.string().max(20).optional(),
    hq: z.string().max(120).optional(),
    note: z.string().max(2000).optional(),
    authorized: z.number().int().min(0).max(100000).optional(),
    commanderId: z.string().max(120).optional(),
});
/** Én ORBAT-hendelse (event sourcing). `reduceOrbat` bygger tilstanden. */
export const OrbatEventSchema = z.discriminatedUnion('op', [
    z.object({ op: z.literal('create'), unit: OrbatUnitSchema }),
    z.object({ op: z.literal('update'), id: z.string().max(64), patch: OrbatUnitPatchSchema }),
    z.object({ op: z.literal('move'), id: z.string().max(64), parentId: z.string().max(64).optional() }),
    z.object({ op: z.literal('delete'), id: z.string().max(64) }),
    z.object({ op: z.literal('delegate'), id: z.string().max(64), commanderId: z.string().max(120).optional() }),
]);
/** Er `descId` en etterkommer av (eller lik) `ancestorId`? Brukes til å hindre
 * at en node flyttes under sin egen undergren (som ville lage en syklus). */
function isDescendant(units, ancestorId, descId) {
    const byId = new Map(units.map((u) => [u.id, u]));
    let cur = descId;
    const seen = new Set();
    while (cur && !seen.has(cur)) {
        if (cur === ancestorId)
            return true;
        seen.add(cur);
        cur = byId.get(cur)?.parentId;
    }
    return false;
}
/** Er `overordnetId` en ekte forfader til `underordnetId` i kommandolinja?
 * «Direkte linje» betyr langs parent-kjeden — en sidegren teller ikke, og en
 * node er ikke sin egen overordnede. Grunnlaget for hvem som kan verifisere en
 * persons ORBAT-tilhørighet (se `medlemskap.ts` og `@skade/rbac`). Syklus-sikker. */
export function orbatErOverordnet(units, overordnetId, underordnetId) {
    if (overordnetId === underordnetId)
        return false; // ikke sin egen overordnede
    const byId = new Map(units.map((u) => [u.id, u]));
    const seen = new Set();
    let cur = byId.get(underordnetId)?.parentId;
    while (cur && !seen.has(cur)) {
        if (cur === overordnetId)
            return true;
        seen.add(cur);
        cur = byId.get(cur)?.parentId;
    }
    return false;
}
/** Kommandolinja fra en node og oppover (noden selv først, deretter forfedre).
 * Ukjent node gir tom liste. Syklus-sikker. */
export function orbatKommandolinje(units, nodeId) {
    const byId = new Map(units.map((u) => [u.id, u]));
    const linje = [];
    const seen = new Set();
    let cur = nodeId;
    while (cur && !seen.has(cur)) {
        const u = byId.get(cur);
        if (!u)
            break;
        linje.push(u);
        seen.add(cur);
        cur = u.parentId;
    }
    return linje;
}
/** Bruk én hendelse på en enhetsliste. Ren (returnerer ny liste). */
export function applyOrbatEvent(units, ev) {
    switch (ev.op) {
        case 'create': {
            if (units.some((u) => u.id === ev.unit.id)) {
                return units.map((u) => (u.id === ev.unit.id ? ev.unit : u)); // idempotent upsert
            }
            return [...units, ev.unit];
        }
        case 'update':
            return units.map((u) => (u.id === ev.id ? { ...u, ...ev.patch } : u));
        case 'move': {
            // Ingen selv-forelder og ingen flytting under egen undergren (syklus-vern).
            if (ev.id === ev.parentId)
                return units;
            if (ev.parentId && isDescendant(units, ev.id, ev.parentId))
                return units;
            return units.map((u) => (u.id === ev.id ? { ...u, parentId: ev.parentId } : u));
        }
        case 'delete': {
            // Kaskade: fjern noden OG hele undergrenen (ingen foreldreløse noder).
            const drop = new Set([ev.id]);
            let grew = true;
            while (grew) {
                grew = false;
                for (const u of units) {
                    if (u.parentId && drop.has(u.parentId) && !drop.has(u.id)) {
                        drop.add(u.id);
                        grew = true;
                    }
                }
            }
            return units.filter((u) => !drop.has(u.id));
        }
        case 'delegate':
            return units.map((u) => (u.id === ev.id ? { ...u, commanderId: ev.commanderId } : u));
    }
}
/** Bygg full ORBAT-tilstand fra en hendelsesstrøm. Ren + testbar. Stier
 * materialiseres til slutt slik at `path` alltid stemmer med treet. */
export function reduceOrbat(events) {
    const units = events.reduce((acc, ev) => applyOrbatEvent(acc, ev), []);
    return withOrbatPaths(units);
}
export const READINESS_LABEL = {
    green: 'Full stridsevne', amber: 'Redusert', red: 'Kritisk', black: 'Ikke stridsdyktig',
};
export const READINESS_COLOR = {
    green: '#4bd15b', amber: '#ffd24a', red: '#ff3b3b', black: '#1a1a1a',
};
const READINESS_RANK = { black: 0, red: 1, amber: 2, green: 3 };
/** Personellstyrke → stridsevne (andel stridsdyktige av oppsatt/kjent styrke). */
export function strengthReadiness(p, authorized) {
    const base = authorized && authorized > 0 ? authorized : p.total;
    if (base <= 0)
        return 'black';
    const ratio = p.ok / base;
    if (ratio >= 0.85)
        return 'green';
    if (ratio >= 0.6)
        return 'amber';
    if (ratio >= 0.3)
        return 'red';
    return 'black';
}
/** Forsyningsnivå → stridsevne (direkte kartlegging). */
export function supplyReadiness(worst) {
    if (!worst)
        return 'green'; // ingen data → anta ok (ikke straff manglende rapport)
    return worst;
}
/** Kombinert stridsevne = det verste av personell og forsyning. */
export function combatReadiness(p, worstSupply, authorized) {
    const a = strengthReadiness(p, authorized);
    const b = supplyReadiness(worstSupply);
    return READINESS_RANK[a] <= READINESS_RANK[b] ? a : b;
}
/** Verste stridsevne i et subtre (rollup). Enhetens egen readiness slås sammen
 * med barnas. `readinessOf` gir readiness for en enkelt enhet (id). */
export function rollupReadiness(node, readinessOf) {
    let worst = readinessOf(node.unit.id);
    for (const child of node.children) {
        const cr = rollupReadiness(child, readinessOf);
        if (READINESS_RANK[cr] < READINESS_RANK[worst])
            worst = cr;
    }
    return worst;
}
//# sourceMappingURL=orbat.js.map